import { ArrowLeft, ExternalLink } from "lucide-react";
import { Link, useLocation } from "react-router";

/**
 * Settings hub (HubSpot-pattern): grouped left rail + content pane. Every
 * page states its scope - personal settings only affect you, workspace
 * settings affect everyone. Users & Teams links out to the existing users
 * resource pages, like HubSpot's rail links out to Billing.
 */

interface RailEntry {
  to: string;
  label: string;
  /** Renders an external-style arrow (page lives outside the hub shell). */
  external?: boolean;
}

interface RailGroup {
  label: string;
  entries: RailEntry[];
}

const RAIL: RailGroup[] = [
  {
    label: "Your preferences",
    entries: [{ to: "/settings/profile", label: "My profile" }],
  },
  {
    label: "Account management",
    entries: [
      { to: "/settings/account-defaults", label: "Account defaults" },
      { to: "/settings/signals", label: "Keep an eye on" },
      { to: "/sales", label: "Users & teams", external: true },
      { to: "/settings/billing", label: "Plan & billing" },
      { to: "/settings/integrations", label: "Integrations" },
    ],
  },
  {
    label: "Data management",
    entries: [
      { to: "/settings/objects", label: "Objects" },
      { to: "/settings/lifecycle", label: "Lifecycle stages" },
      { to: "/settings/custom-fields", label: "Custom fields" },
      { to: "/settings/import-export", label: "Import" },
      { to: "/settings/tracking", label: "Tracking code" },
      { to: "/settings/email-capture", label: "Email capture" },
      { to: "/settings/privacy", label: "Privacy & consent" },
    ],
  },
];

export interface SettingsMeta {
  title: string;
  scope: "personal" | "workspace";
  note?: string;
}

export const SETTINGS_META: Record<string, SettingsMeta> = {
  "/settings/profile": {
    title: "My profile",
    scope: "personal",
  },
  "/settings/account-defaults": {
    title: "Account defaults",
    scope: "workspace",
    note: "Branding, company identity, and the workspace timezone.",
  },
  "/settings/signals": {
    title: "Keep an eye on",
    scope: "workspace",
    note: "The rules behind the dashboard's signal panel - heating up, slipping deals, overdue follow-ups.",
  },
  "/settings/billing": {
    title: "Plan & billing",
    scope: "workspace",
    note: "Your seats and contact allowance. Limits are managed by Grey Matter.",
  },
  "/settings/integrations": {
    title: "Integrations",
    scope: "workspace",
  },
  "/settings/objects": {
    title: "Objects",
    scope: "workspace",
    note: "Changing stages and types affects existing records and reports for everyone.",
  },
  "/settings/lifecycle": {
    title: "Lifecycle stages",
    scope: "workspace",
    note: "The funnel ladder feeds the funnel chart, lists, and workflows.",
  },
  "/settings/custom-fields": {
    title: "Custom fields",
    scope: "workspace",
    note: "Fields you add here appear for all users.",
  },
  "/settings/import-export": {
    title: "Import",
    scope: "workspace",
  },
  "/settings/privacy": {
    title: "Privacy & consent",
    scope: "workspace",
    note: "Unsubscribes and data requests land here. CAN-SPAM opt-outs are honored automatically.",
  },
  "/settings/email-capture": {
    title: "Email capture",
    scope: "workspace",
    note: "BCC/forward mail that didn't match exactly one contact waits here for a decision.",
  },
  "/settings/tracking": {
    title: "Tracking code",
    scope: "workspace",
    note: "Put this on your website to see where leads come from and what they do before they reach out.",
  },
};

export const SettingsLayout = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const meta = SETTINGS_META[location.pathname];
  return (
    <div className="flex gap-8 mt-4 pb-20">
      {/* Rail */}
      <nav className="hidden md:block w-52 shrink-0">
        <div className="sticky top-4">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground px-3"
          >
            <ArrowLeft className="w-3 h-3" /> Back to CRM
          </Link>
          <h1 className="text-2xl font-semibold px-3 mt-2 mb-3">Settings</h1>
          <div className="flex flex-col gap-4">
            {RAIL.map((group) => (
              <div key={group.label}>
                <p className="px-3 pb-1 text-[10px] uppercase tracking-wide text-muted-foreground font-medium">
                  {group.label}
                </p>
                <div className="flex flex-col">
                  {group.entries.map((e) => {
                    const active = location.pathname === e.to;
                    return (
                      <Link
                        key={e.to}
                        to={e.to}
                        className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition-colors ${
                          active
                            ? "bg-primary/10 text-primary font-medium"
                            : "hover:bg-muted text-foreground/80 hover:text-foreground"
                        }`}
                      >
                        <span className="flex-1">{e.label}</span>
                        {e.external && (
                          <ExternalLink className="w-3 h-3 text-muted-foreground" />
                        )}
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="flex-1 min-w-0 max-w-2xl">
        {meta && (
          <div className="mb-4">
            <h2 className="text-xl font-semibold">{meta.title}</h2>
            <p className="text-sm text-muted-foreground">
              {meta.scope === "personal"
                ? "These preferences only apply to you."
                : "These settings apply to everyone in this workspace."}
              {meta.note ? ` ${meta.note}` : ""}
            </p>
          </div>
        )}
        {children}
      </div>
    </div>
  );
};
